import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewChild} from '@angular/core';
import {ConfirmationService, MenuItem, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {CommonFieldService} from '../../../../services/common/common-field.service';
import {Trade} from '../../../../model/clearing/trade';
import {InternalTradeOrderViewService} from '../../../../services/internal/internal-trade-order-view.service';
import {Observable, takeUntil, timer} from "rxjs";
import {ApiValidation} from "../../../../model/api/api-validation";
import {ClearingSelectionInfo} from "../../../../model/clearing/clearing-selection-info";
import {TableButtonConfig} from "../../../../model/api/table-button-config";
import {ClearingAllocationTaskService} from "../../../../services/clearing/clearing-allocation-task.service";
import {
  SingleAllocationFormComponent
} from "../form/allocation/single-allocation-form/single-allocation-form.component";
import {SplitAllocationFormComponent} from "../form/allocation/split-allocation-form/split-allocation-form.component";
import {ViewConfig} from "../../../../model/api/view-config";
import {
  SingleAllocationMultiExchangeFormComponent
} from "../form/allocation/single-allocation-multi-exchange-form/single-allocation-multi-exchange-form.component";
import {ClearingAllocationService} from "../../../../services/clearing/clearing-allocation.service";
import {DatePipe, DecimalPipe} from "@angular/common";
import {
  OverrideCustomReferenceFormComponent
} from "../form/override-custom-reference-form/override-custom-reference-form.component";
import {ThemeService} from "../../../../services/tools/theme.service";
import {ClearingInfoLoadRequest} from "../../../../model/clearing/clearing-info-load-request";
import {AppConfigService} from "../../../../services/app-config.service";
import {CreateOrderFormComponent} from "../form/allocation/create-order-form/create-order-form.component";
import {ClearingTableBaseWsComponent} from "../clearing-table-base-ws.component";
import {ClearingTradeWsService} from "../../../../services/clearing/ws/clearing-trade-ws.service";
import {ClearingTableConfigService} from "../../../../services/clearing/clearing-table-config.service";
import {ClearingCookieService} from "../../../../services/clearing/clearing-cookie-service";
import {ClearingSelectionService} from "../../../../services/clearing/clearing-selection.service";
import {UpdateTradePriceFormComponent} from "../form/update-trade-price-form/update-trade-price-form.component";
import {CCRoles, CCRolesType} from "../../../../model/security/cc-roles-type";
import {ClearingTableSyncWsService} from "../../../../services/clearing/ws/clearing-table-sync-ws.service";
import {ClearingTradeInfoService} from "../../../../services/clearing/clearing-trade-info.service";

@Component({
  template: '',
  providers: [
    MessageService,
    ConfirmationService,
    TranslateService,
    DatePipe,
    DecimalPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TradeTableBaseComponent extends ClearingTableBaseWsComponent {

  // table
  headerButtonConfig: TableButtonConfig[] = [];
  tableContextMenuConfig: MenuItem[] = [];
  transPartKey: string = 'clearing.trade.selection';

  // form
  @ViewChild('singleAllocationDialog')
  singleAllocationDialog: SingleAllocationFormComponent;
  @ViewChild('singleAllocationMultiExchangeDialog')
  singleAllocationMultiExchangeDialog: SingleAllocationMultiExchangeFormComponent;
  @ViewChild('splitAllocationDialog')
  splitAllocationDialog: SplitAllocationFormComponent;
  @ViewChild('createOrderDialog')
  createOrderDialog: CreateOrderFormComponent;
  @ViewChild('overrideCustomReferenceDialog')
  overrideCustomReferenceDialog: OverrideCustomReferenceFormComponent;
  @ViewChild('updateTradePriceDialog')
  updateTradePriceDialog: UpdateTradePriceFormComponent;

  constructor(cdref: ChangeDetectorRef,
              allocationService: ClearingAllocationService,
              allocationTaskService: ClearingAllocationTaskService,
              protected commonFieldService: CommonFieldService,
              protected userViewService: InternalTradeOrderViewService,
              appConfigService: AppConfigService,
              clearingWsService: ClearingTradeWsService,
              messageService: MessageService,
              confirmationService: ConfirmationService,
              public translateService: TranslateService,
              public themeService: ThemeService,
              public tableConfigService: ClearingTableConfigService,
              protected clearingCookieService: ClearingCookieService,
              protected clearingTableSyncWsService: ClearingTableSyncWsService,
              protected clearingSelectionService: ClearingSelectionService,
              protected clearingTradeInfoService: ClearingTradeInfoService) {
    super(cdref, messageService, confirmationService,
      appConfigService, clearingWsService, translateService, commonFieldService, allocationTaskService,
      allocationService, themeService, tableConfigService, clearingCookieService, clearingTableSyncWsService,
      clearingSelectionService);
    userViewService.clearStorage();
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
    this.themeService.isDarkThemeChange.subscribe(value => {
      this.clearReloadTable(this.tableCurrentRequest);
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  public initConfigs() {
    super.initConfigs();

    if (this.appConfigService.clearingAllocationEnabled)
      this.initCommandButtons();

  }

  protected initCommandButtons() {
    this.tableContextMenuConfig = [];
    if (!this.isReadOnly()) {
      this.headerButtonConfig.push(
        {
          separator: true,
        },
        {
          tooltip: this.getTranslation('page.' + this.transPartKey + '.table.split_allocation'),
          icon: 'fas fa-users',
          class: ' p-button-success',
          command: records => this.splitAllocation(this.getTrueSelectedTradeIds(), this.currentClearingSelectionInfosByLeg),
          //this.allocateFromSelection(),
          enabled: () => this.splitAllocationEnabled(this.currentClearingSelectionInfosByLeg)
        },
        {
          tooltip: this.getTranslation('page.' + this.transPartKey + '.table.single_allocation'),
          icon: 'fas fa-user',
          class: ' p-button-success',
          command: records => this.singleAllocation(this.getTrueSelectedTradeIds(), this.currentClearingSelectionInfosByLeg),
          enabled: () => this.singleAllocationEnabled()
        },
      );
      if (this.isRoleOrderPrecreation()) {
        this.headerButtonConfig.push(
          {
            tooltip: this.getTranslation('page.' + this.transPartKey + '.table.create_order'),
            icon: 'fas fa-user cc-fa-create-order',
            class: ' p-button-success',
            command: records => this.createOrder(),
            enabled: () => true
          },
        );
      }
      this.headerButtonConfig.push(
        {
          tooltip: this.getTranslation('page.' + this.transPartKey + '.table.override_custom_reference'),
          icon: 'fas fa-pen-to-square',
          class: ' p-button-help',
          command: records => this.overrideCustomReference(),
          enabled: () => this.overrideCustomReferenceEnabled()
        },
      );

      if (this.isTradePriceUpdateRole()) {
        this.headerButtonConfig.push(
          {
            tooltip: this.getTranslation('page.' + this.transPartKey + '.table.update_trade_price'),
            icon: 'fas fa-pen-to-square cc-fa-update-price',
            class: ' p-button-help',
            command: records => this.updateTradePrice(),
            enabled: () => this.updateTradePriceEnabled()
          },
        )
      }

      this.tableContextMenuConfig.push(
        {
          id: 'split-allocation',
          label: this.getTranslation('page.' + this.transPartKey + '.table.split_allocation'),
          icon: 'fas fa-users',
          command: () => this.splitAllocation(this.getTrueSelectedTradeIds(), this.currentClearingSelectionInfosByLeg),
          disabled: this.splitAllocationEnabled(this.currentClearingSelectionInfosByLeg)
        },
        {
          id: 'single-allocation',
          label: this.getTranslation('page.' + this.transPartKey + '.table.single_allocation'),
          icon: 'fas fa-user',
          command: () => this.singleAllocation(this.getTrueSelectedTradeIds(), this.currentClearingSelectionInfosByLeg),
          disabled: this.singleAllocationEnabled()
        },
        {
          id: 'overrideCustomReference',
          label: this.getTranslation('page.' + this.transPartKey + '.table.override_custom_reference'),
          icon: 'fas fa-pen-to-square',
          command: () => this.overrideCustomReference(),
          disabled: this.overrideCustomReferenceEnabled()
        });

      if (this.isTradePriceUpdateRole()) {
        this.tableContextMenuConfig.push(
          {
            id: 'updateTradePrice',
            label: this.getTranslation('page.' + this.transPartKey + '.table.update_trade_price'),
            icon: 'fas fa-pen-to-square cc-fa-update-price',
            command: () => this.updateTradePrice(),
            disabled: this.updateTradePriceEnabled()
          });
      }

    }
  }

  protected getViewTemplate(): Observable<ApiValidation<ViewConfig>> {
    return this.userViewService.getTradeTemplate(this.userView.template)
  }

  public allocateFromSelection(selectIds?: string[], forceSingle: boolean = false) {
    this.setLoading();
    // setTimeout(() => {
    // this.tableLoadingCount++;
    if (!selectIds) {
      selectIds = [...this.getTrueSelectedTradeIds()];
    }
    const request: ClearingInfoLoadRequest = new ClearingInfoLoadRequest(selectIds);

    this.getSelectionInfoLegs(request).subscribe(apiValidation => {
      if (apiValidation.success) {
        if (!forceSingle && this.splitAllocationEnabled(apiValidation.item))
          this.splitAllocation(selectIds, apiValidation.item);
        else
          this.singleAllocation(selectIds, apiValidation.item);
      }
      this.setNotLoading();
      // this.tableLoadingCount--;
    });
    // },250);
  }

  approveTradeAllocation(request: ClearingInfoLoadRequest , clearingSelectionInfosByLeg: ClearingSelectionInfo[],  onApproved: (infos: ClearingSelectionInfo[], ids: string[]) => void) {
    const currentSelectedIds = [...this.selectedIds];
    this.clearingTradeInfoService.getTradeAllocationApproval(request).pipe(takeUntil(this.destroy$)).subscribe(apiValidation => {
      if(apiValidation.success){
        if(apiValidation.item){
          onApproved(clearingSelectionInfosByLeg, currentSelectedIds);
        } else {
          const message = this.getTranslation('page.clearing.trade.table.trade_allocation_prohibited');
          this.warnMessage(message);
        }
      } else {
        this.errorMessage(apiValidation.message);
      }
    });
  }

  public doSingleAllocation(clearingSelectionInfosByLeg: ClearingSelectionInfo[], currentSelectedIds: string[]) {
    const exchanges = [];
    const currentClearingSelectionInfos: ClearingSelectionInfo[] = [...clearingSelectionInfosByLeg];
    // console.log("------> currentClearingSelectionInfos "+JSON.stringify(this.currentClearingSelectionInfos));
    currentClearingSelectionInfos.forEach(value => {
      if (!exchanges.includes(value.exchangeCode))
        exchanges.push(value.exchangeCode);
    });
    if (exchanges.length == 1) {
      this.singleAllocationDialog.open(currentSelectedIds, currentClearingSelectionInfos);
    }
    else {
      this.singleAllocationMultiExchangeDialog.open(currentSelectedIds, currentClearingSelectionInfos);
    }
    this.setNotLoading();
    // this.tableLoadingCount--;
  }

  public doSplitAllocation(clearingSelectionInfosByLeg: ClearingSelectionInfo[], currentSelectedIds: string[]) {
    const currentClearingSelectionInfos: ClearingSelectionInfo[] = [...clearingSelectionInfosByLeg];
    this.allocationService.getSignificantTrades(currentSelectedIds).subscribe(validation => {
      if (validation.success) {
        this.splitAllocationDialog.open(currentSelectedIds, validation.item[0], currentClearingSelectionInfos);
      } else {
        this.errorMessage(validation.message);
      }
      this.setNotLoading();
      // this.tableLoadingCount--;
    });
  }

  public singleAllocation(selectIds: string[], clearingSelectionInfosByLeg: ClearingSelectionInfo[] = undefined) {
    const currentSelectedIds = [...selectIds];
    const request: ClearingInfoLoadRequest = new ClearingInfoLoadRequest(selectIds);
    this.setLoading();
    // this.tableLoadingCount++;
    if (clearingSelectionInfosByLeg) {
      //check trade allocation valide
        this.approveTradeAllocation(request, clearingSelectionInfosByLeg, (infos, ids) => this.doSingleAllocation(infos, ids))

    } else {
      this.getSelectionInfoLegs(request).subscribe(apiValidation => {
        if (apiValidation.success && apiValidation.item) {
          this.singleAllocation(currentSelectedIds, apiValidation.item)
        }
        this.setNotLoading();
        // this.tableLoadingCount--;
      });
    }
  }

  public createOrder() {
    this.createOrderDialog.open();
  }

  public splitAllocation(selectIds: string[], clearingSelectionInfosByLeg: ClearingSelectionInfo[] = undefined) {
    const currentSelectedIds = [...selectIds];
    const request: ClearingInfoLoadRequest = new ClearingInfoLoadRequest(selectIds);
    this.setLoading();
    // this.tableLoadingCount++;
    if (clearingSelectionInfosByLeg) {
        this.approveTradeAllocation(request, clearingSelectionInfosByLeg, (infos, ids) => this.doSplitAllocation(infos, ids))
    } else {
      const request: ClearingInfoLoadRequest = new ClearingInfoLoadRequest(selectIds);

      this.getSelectionInfoLegs(request).subscribe(apiValidation => {
        if (apiValidation.success && apiValidation.item) {
          this.splitAllocation(currentSelectedIds, apiValidation.item)
        }
        this.setNotLoading();
        // this.tableLoadingCount--;
      });
    }
  }

  protected overrideCustomReference(currentSelectedIds?: string[]) {
    if (!currentSelectedIds) {
      currentSelectedIds = [...this.getTrueSelectedTradeIds()];
    }
    this.setLoading();
    // this.tableLoadingCount++;
    if (currentSelectedIds.length > 0) {
      this.allocationService.getSignificantTrades(currentSelectedIds).subscribe(validation => {
        if (validation.success) {
          const firstTrade: Trade = validation.item[0];
          this.overrideCustomReferenceDialog.open(currentSelectedIds, firstTrade);
        } else {
          this.errorMessage(validation.message);
        }
        this.setNotLoading();
        // this.tableLoadingCount--;
      });
    }
  }

  protected updateTradePrice(currentSelectedIds?: string[]) {
    if (!currentSelectedIds) {
      currentSelectedIds = [...this.getTrueSelectedTradeIds()];
    }
    this.tableLoadingCount++;
    if (currentSelectedIds.length > 0) {
      this.allocationService.getSignificantTrades(currentSelectedIds).subscribe(validation => {
        if (validation.success) {
          const firstTrade: Trade = validation.item[0];
          this.updateTradePriceDialog.open(currentSelectedIds, firstTrade);
        } else {
          this.errorMessage(validation.message);
        }
        this.tableLoadingCount--;
      });
    }
  }

  refreshContextMenu() {
    this.tableContextMenuConfig.forEach(menu => {
      if (menu.id === 'split-allocation') {
        menu.disabled = !this.splitAllocationEnabled(this.currentClearingSelectionInfosByLeg);
      } else if (menu.id === 'single-allocation') {
        menu.disabled = !this.singleAllocationEnabled();
      } else if (menu.id === 'overrideCustomReference') {
        menu.disabled = !this.overrideCustomReferenceEnabled();
      } else if (menu.id === 'updateTradePrice') {
        menu.disabled = !this.updateTradePriceEnabled();
      }
    });
    this.tableContextMenuConfig = [...this.tableContextMenuConfig];
    this.markForCheck();
  }

  onSingleAllocationSubmit(event: any) {
    this.startCheckingAllocationTask(event.taskId, allocationtask => {
      setTimeout(() => {
        this.singleAllocationDialog.openWithRecord(event.formRecord);
      }, 250);
    });
  }

  onSingleAllocationMultiExchangeSubmit(event: any) {
    this.startCheckingAllocationTask(event.taskId, allocationtask => {
        setTimeout(() => {
          this.singleAllocationMultiExchangeDialog.openWithRecord(event.formRecord);
        }, 250);
      }
    );
  }

  onSplitAllocationSubmit(event: any) {
    this.startCheckingAllocationTask(event.taskId, allocationtask => {
      setTimeout(() => {
        this.splitAllocationDialog.openWithRecord(event.formRecord);
      }, 250);
    });
  }

  onUpdateTradePrice(event: any) {
    this.startCheckingAllocationTask(event.taskId, allocationtask => {
      setTimeout(() => {
        this.overrideCustomReferenceDialog.openWithRecord(event.formRecord);
      }, 250);
    });
  }

  onCreateOrder(event: any) {
    this.startCheckingAllocationTask(event.taskId, allocationtask => {
      setTimeout(() => {
        this.createOrderDialog.openWithRecord(event.formRecord);
      }, 250);
    });
  }

  protected splitAllocationEnabled(clearingSelectionInfosByLeg: ClearingSelectionInfo[]) {
    let splitAllocationEnabled = false;
    if (clearingSelectionInfosByLeg.length == 1
      && this.getTrueSelectedTradeIds().length <= this.appConfigService.allocationHardLimitCount) {
      splitAllocationEnabled = true;
    }
    return splitAllocationEnabled;
  }

  protected singleAllocationEnabled() {
    return this.getTrueSelectedTradeIds().length > 0
    && this.getTrueSelectedTradeIds().length <= this.appConfigService.allocationHardLimitCount
    && this.currentClearingSelectionInfosByLeg.length > 0 && !this.hasMultipleOwners();
  }

  protected overrideCustomReferenceEnabled() {
    return this.getTrueSelectedTradeIds().length > 0
      && this.getTrueSelectedTradeIds().length <= this.appConfigService.allocationHardLimitCount
      && this.currentClearingSelectionInfosByLeg.length > 0 && !this.hasMultipleOwners();
  }

  protected updateTradePriceEnabled() {
    return this.getTrueSelectedTradeIds().length > 0
      && this.getTrueSelectedTradeIds().length <= this.appConfigService.allocationHardLimitCount
      && this.currentClearingSelectionInfosByLeg.length > 0 && !this.hasMultipleOwners();
  }

  activeChanged() {
    this.selectedIds = [];
    this.selectedIdsChange.emit(this.selectedIds);
    this.active = !this.active;
    this.activeChange.emit(this.active);
  }

  protected getTrueSelectedTradeIds(selectedIds: string[] = this.selectedIds): string[] {
    return selectedIds;
  }

  //
  // protected shift_s(event) {
  //   if (!this.isReadOnly() && this.singleAllocationEnabled()) {
  //     this.allocateFromSelection(undefined);
  //   }
  // }


  isTradePriceUpdateRole(): boolean {
    return !this.appConfigService.clearingAllocationEnabled ||
      (this.userRoles?.some(role => CCRoles.ROLES_CLEARING_TRADE_PRICE_UPDATE.includes(role)));
  }

  protected isRoleOrderPrecreation() {
    return this.hasRoles(CCRoles.ROLES_CLEARER_ORDER_PRECREATE)
  }
}
